import React, { useMemo } from 'react';
import { Box, Stack, TableHead, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  CustomTable,
  CustomTableBody,
  CustomTableCell,
  CustomTableContainer,
  CustomTableHeadCell,
  CustomTableRow,
} from 'src/components/MainPage/_shared/Table';
import { TextTruncated } from 'src/uikit/TextTruncated';
import { useUnitOfMeasurements } from '../../shared/useUnitOfMeasurements';
import {
  INCOME_ITEM_COLUMNS,
  ITEM_NAME_CELL_WIDTH,
  TABLE_STYLES,
} from '../constants';
import {
  formatPrice,
  getItemTotalPrice,
  getUnitName,
} from '../../WarehouseIncomesPage/helpers';
import { WarehouseIncomeItemDetail } from '../../WarehouseIncomesPage/services';
import { NoItemsCell } from './NoItemsCell';

type Props = {
  items: WarehouseIncomeItemDetail[];
};

export const ItemsReadonlyTable = ({ items }: Props) => {
  const { unitsMap } = useUnitOfMeasurements();

  const totalSum = useMemo(
    () => items.reduce((sum, item) => sum + getItemTotalPrice(item), 0),
    [items]
  );

  return (
    <Stack flex={1}>
      <CustomTableContainer
        data-testid="warehouse-income-items_table"
        sx={{ height: 'auto' }}
      >
        <CustomTable stickyHeader fullHeight={!items?.length} sx={TABLE_STYLES}>
          <TableHead>
            <CustomTableRow withDivider keepSticky>
              {INCOME_ITEM_COLUMNS.map(({ key, label }) => (
                <CustomTableHeadCell key={key}>{label}</CustomTableHeadCell>
              ))}
            </CustomTableRow>
          </TableHead>

          <CustomTableBody>
            {items.map((item) => {
              const unitName = getUnitName(
                unitsMap,
                item.nomenclature?.unitOfMeasurement
              );
              const total = getItemTotalPrice(item);

              return (
                <CustomTableRow key={item.id}>
                  <CustomTableCell>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <TextTruncated
                        text={item.nomenclature?.name || '—'}
                        maxWidth={ITEM_NAME_CELL_WIDTH}
                      />
                      {unitName && (
                        <Typography variant="caption" color="text.secondary">
                          ({unitName})
                        </Typography>
                      )}
                    </Stack>
                  </CustomTableCell>
                  <CustomTableCell>{item.quantity}</CustomTableCell>
                  <CustomTableCell>
                    {formatPrice(item.purchasePrice)}
                  </CustomTableCell>
                  <CustomTableCell>{formatPrice(total)}</CustomTableCell>
                  <CustomTableCell>{item.batchNumber || '—'}</CustomTableCell>
                  <CustomTableCell>
                    <TextTruncated
                      text={item.comment || '—'}
                      maxWidth={ITEM_NAME_CELL_WIDTH}
                    />
                  </CustomTableCell>
                </CustomTableRow>
              );
            })}
            {items.length === 0 && (
              <NoItemsCell
                colSpan={INCOME_ITEM_COLUMNS.length}
                text={'Номенклатуры не добавлены'}
              />
            )}
          </CustomTableBody>
        </CustomTable>
      </CustomTableContainer>
      {items.length > 0 && (
        <Box sx={{ padding: '0.75rem 1.25rem' }}>
          <Typography variant="h6">
            <strong>Итого:</strong> {formatPrice(totalSum)}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
